// Mirrors server/src/sim/genome.ts arrays so the client renders the real look
// from the compact `app` codes [speciesIdx, paletteIdx, accessoryIdx].
export const SPECIES = [
    // indices 0-9 are the original species — their rendering is FROZEN (existing pets keep their look)
    'cyber-cat', 'mecha-shiba', 'mochi-blob', 'neon-axolotl', 'fox-droid',
    'capybara-mech', 'pixel-bun', 'tanuki-bot', 'jelly-sprite', 'origami-crane',
    // indices 10+ are new species with distinct silhouettes (only new pets roll into these)
    'puffer-pup', 'lantern-moth', 'pebble-tortoise', 'sprout-deer', 'ghost-kit', 'coral-otter',
];
export const PALETTES: [string, string, string][] = [
    ['#ffd9e8', '#ff7eb6', '#7a3b5d'], ['#d6f5ff', '#46c8ff', '#1b6e94'],
    ['#fff3c4', '#ffcf4d', '#9a7220'], ['#e6ffe0', '#7be06a', '#2f7a36'],
    ['#efe2ff', '#b07bff', '#5a3a8f'], ['#ffe2cc', '#ff9b54', '#a85a26'],
    ['#dfeaff', '#7e9bff', '#3a4f99'], ['#ffe0f0', '#ff5fa2', '#9c2f63'],
    ['#d9fff4', '#3fe0c0', '#176f5e'], ['#f0f0f5', '#aab0c0', '#5b6072'],
];
export const ACCESSORIES = ['headphones', 'scarf', 'goggles', 'beanie', 'tiny-backpack', 'flower-pin', 'cape', 'glasses', 'none', 'none'];

export function palette(app: [number, number, number]): [string, string, string] {
    return PALETTES[app[1] % PALETTES.length]!;
}
export function speciesName(app: [number, number, number]): string {
    return SPECIES[app[0] % SPECIES.length]!;
}

/** Earshape/body silhouette varies by species index → distinct cute shapes. (Indices 0-9 only;
 *  FROZEN so existing pets never change. New species 10+ use SPECIES_SPECS below.) */
export function bodyShape(app: [number, number, number]): 'round' | 'tall' | 'blobby' | 'angular' {
    const s = app[0] % SPECIES.length;
    if (s === 2 || s === 8) return 'blobby';
    if (s === 1 || s === 7 || s === 4) return 'angular';
    if (s === 9 || s === 6) return 'tall';
    return 'round';
}

export const LEGACY_SPECIES = 10;   // indices < this render via bodyShape + generic ears (frozen)
export type BodySil = 'round' | 'tall' | 'blobby' | 'angular' | 'low';
export type EarStyle = 'pointy' | 'floppy' | 'round' | 'antenna' | 'frills' | 'none';
export type Feature = 'none' | 'tail' | 'shell' | 'leafhorn' | 'ghosttail' | 'wings' | 'whiskers';
export interface SpeciesSpec { shape: BodySil; ears: EarStyle; feature: Feature; }
/** Distinct silhouettes for the new species (index → spec). Body + ears + a signature feature. */
export const SPECIES_SPECS: Record<number, SpeciesSpec> = {
    10: { shape: 'round',  ears: 'floppy',  feature: 'tail' },       // puffer-pup — droopy ears + a little tail
    11: { shape: 'tall',   ears: 'antenna', feature: 'wings' },      // lantern-moth — antennae + soft wings
    12: { shape: 'low',    ears: 'none',    feature: 'shell' },      // pebble-tortoise — low + a domed shell
    13: { shape: 'tall',   ears: 'pointy',  feature: 'leafhorn' },   // sprout-deer — pointy ears + leaf antlers
    14: { shape: 'round',  ears: 'pointy',  feature: 'ghosttail' },  // ghost-kit — pointy ears + a wispy tail
    15: { shape: 'blobby', ears: 'round',   feature: 'whiskers' },   // coral-otter — round ears + whiskers
};
export function speciesSpec(app: [number, number, number]): SpeciesSpec | null {
    return SPECIES_SPECS[app[0]] ?? null;
}
